import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAIService } from '@/context/AIServiceContext';
import InlineHexagonLoader from '@/components/HexagonLoader/InlineHexagonLoader';
import '@/styles/ITReportStyles.scss';
import * as Sentry from '@sentry/react';

// Increased retry delay to 120 seconds for better rate limit handling
const RETRY_DELAY_MS = 120000; // 120s
const MAX_RETRIES = 3;
// Stagger delay for health commentary (loads after health score data)
const STAGGER_DELAY_MS = 2000; // 2s delay

/**
 * HealthScoreCommentary - AI-generated commentary on the Overall IT Service Health score
 * Provides interpretation and context about what the score means
 * 
 * Features:
 * - Staggered loading (2s delay) to avoid rate limits
 * - Non-blocking (page loads while AI content generates)
 * - Increased retry delays (120s instead of 60s)
 * - Respects AI enabled state from context
 */
export default function HealthScoreCommentary({ healthScoreData }) {
    const { apiFetch } = useAuth();
    const { aiEnabled, isLoading: aiLoading } = useAIService();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [commentary, setCommentary] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const retryTimeoutRef = useRef(null);
    const attemptRef = useRef(0);
    const staggerTimeoutRef = useRef(null);
    const [staggerComplete, setStaggerComplete] = useState(false);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            if (staggerTimeoutRef.current) clearTimeout(staggerTimeoutRef.current);
        };
    }, []);

    // Stagger loading to avoid rate limits
    useEffect(() => {
        // Wait for AI config to load
        if (aiLoading) return;
        
        // If AI is disabled or no health score data, don't fetch
        if (!aiEnabled || !healthScoreData) return;

        // Wait for stagger delay before starting to fetch
        staggerTimeoutRef.current = setTimeout(() => {
            setStaggerComplete(true);
        }, STAGGER_DELAY_MS);

        return () => {
            if (staggerTimeoutRef.current) clearTimeout(staggerTimeoutRef.current);
        };
    }, [aiEnabled, aiLoading, healthScoreData]);

    // Fetch health commentary only after stagger completes and AI is enabled
    useEffect(() => {
        // Wait for AI config to load
        if (aiLoading) return;
        
        // If AI is disabled or no data, don't fetch
        if (!aiEnabled || !healthScoreData || !staggerComplete) return;

        let ignore = false;

        (async () => {
            setLoading(true);
            setError('');

            try {
                const response = await apiFetch('/api/insights/it-health-commentary', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ healthScoreData })
                });

                if (ignore) return;

                if (response && response.ok) {
                    const data = await response.json();
                    if (data && data.commentary) {
                        setCommentary(data.commentary);
                        attemptRef.current = 0;
                    }
                } else if (response && response.status === 204) {
                    // No content - insights disabled or no commentary generated
                    setCommentary('');
                    attemptRef.current = 0;
                } else if (response && response.status === 429) {
                    throw new Error('RATE_LIMIT');
                } else if (response && response.status === 403) {
                    throw new Error('FORBIDDEN');
                } else {
                    throw new Error('Failed to load health score commentary');
                }
            } catch (err) {
                Sentry.captureException(err);
                if (!ignore) {
                    console.error('Error loading health score commentary:', err);
                    const msg = err.message || 'Error';
                    
                    // Rate limit handling with increased retry delay
                    if (msg === 'RATE_LIMIT' && attemptRef.current < MAX_RETRIES) {
                        const attempt = attemptRef.current + 1;
                        attemptRef.current = attempt;
                        setError(`Service busy (429). Retrying in 2 minutes (attempt ${attempt}/${MAX_RETRIES})`);
                        retryTimeoutRef.current = setTimeout(() => {
                            setReloadToken(t => t + 1);
                        }, RETRY_DELAY_MS);
                    } else if (msg === 'FORBIDDEN') {
                        setError('AI Analytics is disabled or API key is invalid.');
                    } else {
                        setError(msg === 'RATE_LIMIT' ? 'Service busy. Please try again later.' : msg);
                    }
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            ignore = true;
        };
    }, [aiEnabled, aiLoading, apiFetch, healthScoreData, staggerComplete, reloadToken]);

    // Show placeholder if AI is loading config
    if (aiLoading) {
        return (
            <div className="ai-commentary-box">
                <div className="ai-commentary-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    <span>AI Health Score Analysis</span>
                </div>
                <div className="ai-commentary-loading">
                    <InlineHexagonLoader />
                    <div style={{ marginTop: 8 }}>Waiting for AI Insights...</div>
                </div>
            </div>
        );
    }
    
    // Don't render if AI is disabled
    if (!aiEnabled) return null;

    // Don't render anything if there's no data
    if (!healthScoreData) return null;

    // Show placeholder while loading
    if (loading && !commentary && !error) {
        return (
            <div className="ai-commentary-box">
                <div className="ai-commentary-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    <span>AI Health Score Analysis</span>
                </div>
                <div className="ai-commentary-loading">
                    <InlineHexagonLoader />
                    <div style={{ marginTop: 8 }}>Generating AI insights...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return null; // Silently fail - don't show errors for optional AI feature
    }

    if (!commentary) {
        return null;
    }

    return (
        <div className="ai-commentary-box">
            <div className="ai-commentary-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                <span>AI Health Score Analysis</span>
            </div>
            <div className="ai-commentary-content">
                {commentary}
            </div>
        </div>
    );
}
